import * as React from 'react';
import { QueryTable, QueryTableColumn } from '../misc/components/queryTable/QueryTable';
import { MainState } from '../Core/reducers';
import { Query, Report, QueryCondition, Permission } from '../misc/api/apiTypes';
import MultiSelectModal from '../misc/components/queryTable/MultiSelectModal';
import { Dispatch, connect } from 'react-redux';
import { FilterReportsAction, ReportFormat, SortReportsAction, InitializeReportsAsyncAction, PageReportsAction, HideWidgetsAction, ToggleReportModalAction } from './ReportActions';
import Alert, { AlertCategoryEnum } from '../misc/components/Alert';
import Loading, { FontSize } from '../misc/components/loading';
import { LocalizedDateTime, DateTimeAccuracy } from '../misc/components/LocalizedDateTime';
import { Authorization } from '../misc/components/Authorization';
import { Link } from 'react-router-dom';
import { PermissionEnum } from '../misc/api/ApiPermissionEnum';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { useSecurityContext } from '../Core/SecurityContext';

declare const rootUrl: string;

export interface ReportsListTableProps {
    query: Query<Report>;
    nameFilterQuery: Query<any>;
    errorMessage: string;
    showReportModal: boolean;

    dispatch?: Dispatch<MainState>;
}

export interface ReportsListTableState {
    showNameFilter: boolean;
    showCreatedDateFilter: boolean;
    showModifiedDateFilter: boolean;
}

export const ReportsListTable = (props: ReportsListTableProps) => {
    const [showNameFilter, setShowNameFilter] = React.useState(false);
    const [showCreatedDateFilter, setShowCreatedDateFilter] = React.useState(false);
    const [showModifiedDateFilter, setShowModifiedDateFilter] = React.useState(false);
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();
    const [legacyClient] = React.useState(useWebPortalLegacyClient());
    const { currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        const showWidgets = currentUserPermissions.find(item => item.Id === PermissionEnum.ReportServerConnectionSettings);

        if (!showWidgets) {
            props.dispatch(HideWidgetsAction.create());
        }
    }, []);

    const getFilterValues = (member: keyof(Report), condition: QueryCondition): any => {
        return props.query
            && props.query.Filters
            && props.query.Filters.find(item => item.Member === member && item.Condition === condition)
            && props.query.Filters.find(item => item.Member === member && item.Condition === condition).Values;
    }

    const getFilterValue = (member: keyof(Report), condition: QueryCondition): any => {
        return getFilterValues(member, condition) && getFilterValues(member, condition).find(() => true);
    }

    const handlePage = (page: number) => {
        props.dispatch(PageReportsAction.create(page));
        props.dispatch(InitializeReportsAsyncAction.create(legacyClient));
    }

    const handleSort = (member: keyof(Report)) => {
        props.dispatch(SortReportsAction.create(member));
        props.dispatch(InitializeReportsAsyncAction.create(legacyClient));
    }

    const handleFilter = (member: keyof(Report)) => {
        switch (member) {
            case 'Name':
                setShowNameFilter(true);
                break;
            case 'Created':
                setShowCreatedDateFilter(true);
                break;
            case 'Modified':
                setShowModifiedDateFilter(true);
                break;
        }
    }

    const handleFilterClose = () => {
        setShowNameFilter(false);
        setShowCreatedDateFilter(false);
        setShowModifiedDateFilter(false);

        props.dispatch(InitializeReportsAsyncAction.create(legacyClient));
    }

    const handleMultiSelectFilterChange = (member: keyof(Report), values: Array<string>) => {
        props.dispatch(FilterReportsAction.create(member, QueryCondition.Equal, values));
    }

    const handleDateRangeFilterChange = (member: keyof(Report), from: string, to: string) => {
        props.dispatch(FilterReportsAction.create(member, QueryCondition.GreaterThanOrEqual, from));
        props.dispatch(FilterReportsAction.create(member, QueryCondition.LessThanOrEqual, to));
    }

    const handleDeleteReport = async (id: string) => {
        await client.client.deleteDefinedReport(id);
        props.dispatch(InitializeReportsAsyncAction.create(legacyClient));
    }

    const toggleReportModal = (id: string) => {
        if (props.showReportModal)
            props.dispatch(InitializeReportsAsyncAction.create(legacyClient));

        props.dispatch(ToggleReportModalAction.create(id));
    }

    const renderHamburgerMenu = (row, currentPermissions) => {
        if (!row.IsRequestingDownload && study.id) {
            const route = {
                pathname: `/reports/viewer/${encodeURIComponent(row.Path)}`,
                state: row,
            }

            return (
                <div className="text-center">
                    <div className="btn-group" role="group" data-automationid="hamburger-menu-button" data-report-name={row.Name}>
                        <button type="button" className="btn btn-primary btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <i className="fa fa-bars" aria-hidden="true"></i>
                        </button>
                        <ul className="dropdown-menu scrollable-menu" role="menu">
                            <li><Link to={route}>Run Report</Link></li>
                            <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentPermissions}>
                                <li><a onClick={() => { toggleReportModal(row.Id) }}>Edit</a></li>
                            </Authorization>
                            <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentPermissions}>
                                <li><a onClick={() => { handleDeleteReport(row.Id) }}>Delete</a></li>
                            </Authorization>
                        </ul>
                    </div>
                </div>
            );
        } else {
            return <Loading size={FontSize.OneX} />;
        }
    }

    const renderDateTimeOffset = (value) => {
        return value ? <LocalizedDateTime time={value} accuracy={DateTimeAccuracy.Day} /> : '-';
    }

    if (props.query) {
        return (
            <>
                <Alert category={AlertCategoryEnum.Danger} show={props.errorMessage !== null}>
                    {props.errorMessage}
                </Alert>
                <QueryTable query={props.query} rowUniqueIdentifier={row => row.Id} onPage={handlePage} onSort={handleSort} onFilter={handleFilter}>
                    <QueryTableColumn member="Name" header="Report Name" map={(row: Report) => row.Name} sortable={true} filterable={true} />
                    <QueryTableColumn member="" header="" map={(row) => renderHamburgerMenu(row, currentUserPermissions)} />
                </QueryTable>
                <MultiSelectModal header="Report Name Filter" show={showNameFilter} options={props.nameFilterQuery.Data} valueKey="Name" labelKey="Name" values={getFilterValues('Name', QueryCondition.Equal)} onChange={(values) => handleMultiSelectFilterChange('Name', values.map(v => v.Name))} onClose={handleFilterClose} />
            </>
        );
    } else {
        return null;
    }
}

function mapStateToProps(state: MainState) {
    return {
        query: state.reports.listTable.reportsQuery,
        nameFilterQuery: state.reports.listTable.nameFilterQuery,
        errorMessage: state.reports.listTable.errorMessage,
        showReportModal: state.reports.listTable.showReportModal
    }
}

export default connect<ReportsListTableProps, any, void>(mapStateToProps)(ReportsListTable);
